import { useEffect, useRef } from 'react'
import { DatasetStatus, PendingStatus } from '@components'
import { datasetPhases } from '@global/constants'

/**
 * Determine if the tracker should be visible.
 * @param args
 * @param {string} args.status Submissions status
 * @param {string} args.phase Submissions phase
 * @returns
 */
const trackerShouldBeVisible = ({
  status = null,
  phase = null,
}: {
  status: PendingStatus | DatasetStatus | null
  phase: keyof typeof datasetPhases | null
}) => {
  if (!status && !phase) return false

  return (
    phase !== datasetPhases.INDEXING &&
    phase !== datasetPhases.COMPLETED &&
    phase !== datasetPhases.SENDING_TO_OUTSTREAMS &&
    phase !== datasetPhases.WAITING_ON_OUTSTREAMS &&
    status !== PendingStatus.ERROR &&
    status !== PendingStatus.PAUSED &&
    status !== PendingStatus.AWAITING_APPROVAL &&
    status !== PendingStatus.REJECTED &&
    status !== DatasetStatus.APPROVED &&
    status !== DatasetStatus.COMPLETE
  )
}

/**
 * Track when processing is complete; toggling back to the data route;
 * to trigger a new dataset query to get the latest changed values.
 * @param props
 * @param {boolean} props.submissionPhase
 * @param {boolean} props.submissionStatus
 * @param {Function} props.onTrackerNoLongerVisible
 */
export const useProcessingEffects = ({
  submissionPhase,
  submissionStatus,
  updateDataCacheStores,
}: {
  submissionPhase: keyof typeof datasetPhases | null
  submissionStatus: PendingStatus | DatasetStatus | null
  updateDataCacheStores: () => void
}) => {
  const isTrackerVisible = trackerShouldBeVisible({
    status: submissionStatus,
    phase: submissionPhase,
  })
  const trackerWasVisibleRef = useRef(isTrackerVisible)

  // Processing is complete so refetch dataset
  // - Run immediately after tracker is no longer displayed. To
  // ensure we refresh the datasest while the table is visible.
  useEffect(() => {
    const onceTrackerIsNoLongerVisible = trackerWasVisibleRef.current && !isTrackerVisible

    if (onceTrackerIsNoLongerVisible) updateDataCacheStores()

    // Reset trackerRef for next check.
    trackerWasVisibleRef.current = isTrackerVisible
  }, [trackerWasVisibleRef.current, isTrackerVisible])

  return {
    isTrackerVisible,
  }
}
